import type {
  AdminAiAction,
  AdminAiDatasetDetail,
  AdminAiFeature,
  AdminAiLabel,
  AdminAiOutput,
  AdminAiSplit,
} from "api/adminApi";

// AI 관리 데이터셋 화면의 표시·가공 규칙. 화면 컴포넌트는 이 함수들만 부른다 (로직은 여기서 테스트한다).

export const FEATURES: { key: AdminAiFeature; label: string }[] = [
  { key: "thought_record_comment", label: "생각기록지 코멘트" },
  { key: "chat", label: "채팅" },
  { key: "automatic_thought", label: "자동적 사고" },
];

export const ACTION_LABEL: Record<AdminAiAction, string> = {
  field_save: "항목 저장",
  confirm: "확정",
  delete: "삭제",
  send: "전송",
  discard: "폐기",
  deliver: "다시 주기",
  comment_delete: "삭제",
};

export const ACTIONS_BY_FEATURE: Record<AdminAiFeature, AdminAiAction[]> = {
  automatic_thought: ["field_save", "confirm", "delete"],
  chat: ["send"],
  thought_record_comment: ["deliver", "comment_delete"],
};

export const LABELS: AdminAiLabel[] = [
  "as_is",
  "edited",
  "coach_written",
  "cleared",
  "unused",
];

export const LABEL_TEXT: Record<AdminAiLabel, string> = {
  as_is: "그대로",
  edited: "수정",
  coach_written: "코치 작성",
  cleared: "지움",
  unused: "안 씀",
};

// Badge tone — 그대로(ok) / 코치 손이 간 것(info) / 비움·안 씀(idle·warn)
export const LABEL_TONE: Record<AdminAiLabel, "ok" | "warn" | "idle" | "info"> =
  {
    as_is: "ok",
    edited: "info",
    coach_written: "info",
    cleared: "warn",
    unused: "idle",
  };

// 라벨 열 제목 툴팁 — "라벨"은 코치가 AI 출력을 어떻게 썼는지이고, 학습 정답과는 다르다
export const LABEL_HELP =
  "라벨 = 코치가 AI 출력을 어떻게 썼는지 (정규화 비교: 닉네임 치환·공백·끝 문장부호 무시). " +
  "그대로 AS_IS · 수정 EDITED · 코치 작성 COACH_WRITTEN(AI 가 비운 칸) · 지움 CLEARED · 안 씀 UNUSED. " +
  "학습 정답(코치 값, 관리자가 보완하면 보완본)과는 다르다.";

// ---------- 적재 규칙 (데이터셋 화면의 "적재 규칙" 패널) ----------
// 서버 AiDatasetHelper(라이브)·AiDatasetBackfill(과거분)의 규칙을 사람이 읽는 표로 옮긴 것 — 서버 규칙이 바뀌면 같이 고친다.

export type LoadRule = {
  // 코치가 화면에서 한 일
  when: string;
  action: AdminAiAction;
  label: string;
  ai: string;
  coach: string;
  // 원본 키 · 중복 규칙
  dedupe: string;
};

export const LOAD_RULES: Record<AdminAiFeature, LoadRule[]> = {
  automatic_thought: [
    {
      when: "항목 하나 채우거나 고치고 저장 버튼",
      action: "field_save",
      label: "그대로 / 수정 / 코치 작성 / 지움 (그 항목)",
      ai: "그 항목의 AI 초안",
      coach: "저장한 값",
      dedupe: "자동적 사고 id · 저장할 때마다 새 행",
    },
    {
      when: "확정 (코치 노트 확정)",
      action: "confirm",
      label: "전부 그대로면 그대로, 하나라도 다르면 수정 (+항목별 라벨)",
      ai: "8개 항목 AI 초안",
      coach: "확정 시점 8개 항목",
      dedupe: "자동적 사고 id",
    },
    {
      when: "AI 초안으로 만든 자동적 사고 삭제",
      action: "delete",
      label: "안 씀",
      ai: "8개 항목 AI 초안",
      coach: "없음",
      dedupe: "자동적 사고 id",
    },
  ],
  chat: [
    {
      when: "✨ 로 추천 불러와서 전송 (그대로 / 고쳐서 / 전부 지우고 새로 써도)",
      action: "send",
      label: "그대로 / 수정 (+편집 비율, 묶음 전체와 비교)",
      ai: "추천 문장",
      coach:
        "이어 보낸 말풍선 묶음 (다음 내담자·업데이트 메시지 전까지, <split> 로 구분)",
      dedupe: "턴(마지막 내담자 메시지) · 1행, 이어 보내면 갱신",
    },
    {
      when: "추천이 떠 있는데 안 불러오고 직접 써서 전송",
      action: "send",
      label: "안 씀",
      ai: "추천 문장",
      coach: "이어 보낸 말풍선 묶음",
      dedupe: "턴 · 1행",
    },
    {
      when: "추천 없이 답장 (추천이 안 뜬 턴)",
      action: "send",
      label: "코치 작성",
      ai: "없음",
      coach: "이어 보낸 말풍선 묶음",
      dedupe: "턴 · 1행 (뒤늦게 추천을 불러와 보내면 AI 추천·라벨이 붙는다)",
    },
  ],
  thought_record_comment: [
    {
      when: "다시 주기 (버튼·워크플로 실행): 이번 회차에 AI 추천을 넣어서 전달",
      action: "deliver",
      label: "그대로 / 수정 (넣었다 비우면 안 씀)",
      ai: "추천 코멘트",
      coach: "전달된 코멘트",
      dedupe: "추천 id · 1회",
    },
    {
      when: "다시 주기: 코치가 직접 쓴 새 코멘트 (위치 = 바로 앞 내담자 답)",
      action: "deliver",
      label: "코치 작성",
      ai: "없음",
      coach: "전달된 코멘트",
      dedupe: "coach:<editorKey> · 1회",
    },
    {
      when: "추천 코멘트 삭제",
      action: "comment_delete",
      label: "안 씀",
      ai: "추천 코멘트",
      coach: "없음",
      dedupe: "추천 id · 1회",
    },
  ],
};

export const NOT_LOADED: Record<AdminAiFeature, string[]> = {
  automatic_thought: [
    "AI 초안 없이 만든 자동적 사고 (비교할 AI 출력이 없다)",
    "긍정(가치) 자동적 사고 (1차 범위 밖)",
  ],
  chat: [
    "활동 업데이트·자동 안내 메시지 (묶음은 업데이트 메시지 전까지)",
    "보내지 않은 채 다음 추천이 와서 교체된 추천 (코치 액션이 없다)",
  ],
  thought_record_comment: [
    "다시 주기 때 본문에 넣지 않은 AI 추천 — 다음 회차에 넣거나 카드를 지우면 그때 적재",
    "이미 본문에 넣은 추천의 카드만 정리한 삭제 — 다시 주기에서 판정",
    "이전 회차에 전달돼 내담자가 이미 답한 추천 (그 회차에 이미 적재)",
  ],
};

// 영역마다 입력 스냅샷이 무엇인지
export const INPUT_OF: Record<AdminAiFeature, string> = {
  automatic_thought: "생각기록지 대화 (activity_content)",
  chat: "내담자 메시지 창 + 직전 대화 20개 + (추천 턴) 에이전트가 읽은 컨텍스트 발췌. 원문 그대로 (닉네임 치환은 학습 데이터 후처리)",
  thought_record_comment: "다시 주기 시점 생각기록지 본문 + 코멘트 위치",
};

// 영역마다 학습 샘플(user → assistant)이 무엇인지 — sftSample·dpoPairs 와 맞춘다
export const SAMPLE_OF: Record<AdminAiFeature, string> = {
  automatic_thought: "생각기록지 대화 → 8개 항목 JSON",
  chat: "[에이전트 컨텍스트] + [대화] → 코치가 이어 보낸 말풍선 묶음 (<split> 로 구분)",
  thought_record_comment:
    "코멘트 가능한 줄에 [editorKey] 가 붙은 대화 (이번 회차 코멘트는 뺌) → {location, comment}",
};

// 자동적 사고 항목 — 서버 AutomaticThoughtFieldKeyConstants 의 id, 코치 화면 순서
export const AUTOMATIC_THOUGHT_FIELDS: { key: string; label: string }[] = [
  { key: "situation", label: "상황" },
  { key: "thought1ThoughtTrap", label: "생각함정 1" },
  { key: "thought1", label: "생각 1" },
  { key: "thought2ThoughtTrap", label: "생각함정 2" },
  { key: "thought2", label: "생각 2" },
  { key: "emotion", label: "감정" },
  { key: "physicalResponse", label: "신체 반응" },
  { key: "behavior", label: "행동" },
];

const TEXT_FIELD = [{ key: "text", label: "답변" }];

export const fieldLabel = (key: string | null) =>
  AUTOMATIC_THOUGHT_FIELDS.find((f) => f.key === key)?.label ?? key ?? "";

// 출력 표의 행 — 자동적 사고는 8개 항목, 채팅·코멘트는 답변 하나
export const outputFieldsOf = (feature: AdminAiFeature) =>
  feature === "automatic_thought" ? AUTOMATIC_THOUGHT_FIELDS : TEXT_FIELD;

export const SPLITS: { key: AdminAiSplit; label: string }[] = [
  { key: "train", label: "train" },
  { key: "valid", label: "valid" },
  { key: "test", label: "test" },
];

// "2026-09-28T14:22:05.123" → "09-28 14:22"
export const formatOccurredAt = (iso: string) => {
  const [date, time] = iso.split("T");
  if (!date || !time) return iso;
  return `${date.slice(5)} ${time.slice(0, 5)}`;
};

// 편집 비율 0~1 → "수정 32%". 값이 없으면(안 씀 등) 빈 문자열
export const editRatioLabel = (ratio: number | null) =>
  ratio == null ? "" : `수정 ${Math.round(ratio * 100)}%`;

// 채팅 정답 묶음의 말풍선 구분자 — 서버 AiDatasetUtils.CHAT_BUNDLE_SEPARATOR
export const CHAT_SPLIT = "\n<split>\n";

// 채팅 행 입력의 에이전트 컨텍스트 발췌 (추천 턴만)
export type AgentContext = { kind: string; target: string; excerpt: string };

export const agentContextOf = (
  input: Record<string, unknown> | null
): AgentContext[] => {
  const list = input?.agent_context;
  if (!Array.isArray(list)) return [];
  return list.map((c) => ({
    kind: String(c?.kind ?? ""),
    target: String(c?.target ?? ""),
    excerpt: String(c?.excerpt ?? ""),
  }));
};

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

// 목록의 "AI 출력"·"코치 저장값" 칸. 항목 저장은 그 항목, 확정·삭제는 상황(가장 앞 항목)을 대표로 보인다.
export const changeSummary = (
  row: {
    feature: AdminAiFeature;
    field: string | null;
    aiOutput: AdminAiOutput | null;
    coachOutput: AdminAiOutput | null;
  },
  max = 60
): { ai: string; coach: string | null } => {
  const key =
    row.feature !== "automatic_thought" ? "text" : (row.field ?? "situation");
  const ai = row.aiOutput?.[key] ?? "";
  // 목록 한 줄에서는 말풍선 구분을 " / " 로
  const coach = row.coachOutput
    ? (row.coachOutput[key] ?? "").split(CHAT_SPLIT).join(" / ")
    : null;
  return { ai: clip(ai, max), coach: coach == null ? null : clip(coach, max) };
};

// 학습 정답 — 항목마다 관리자 보완본이 있으면 그 값, 없으면 코치 값
export const answerOf = (
  coachOutput: AdminAiOutput | null,
  adminOutput: AdminAiOutput | null
): AdminAiOutput => ({ ...(coachOutput ?? {}), ...(adminOutput ?? {}) });

// 보완 칸 입력 → 저장할 adminOutput. 비운 칸은 빼고, 모두 비면 null (= 코치 값이 정답)
export const toAdminOutput = (
  draft: Record<string, string>
): AdminAiOutput | null => {
  const entries = Object.entries(draft)
    .map(([key, value]) => [key, cleanBubbles(value)] as const)
    .filter(([, value]) => value.trim().length > 0);
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

// 채팅 말풍선 — 빈 말풍선은 빼고 다시 잇는다 (다른 영역 값에는 구분자가 없어 그대로)
const cleanBubbles = (value: string) =>
  value.includes(CHAT_SPLIT)
    ? toBubbles(value)
        .filter((b) => b.trim().length > 0)
        .join(CHAT_SPLIT)
    : value;

/** 채팅 정답 텍스트 ↔ 말풍선 목록 */
export const toBubbles = (text: string): string[] => text.split(CHAT_SPLIT);
export const fromBubbles = (bubbles: string[]): string =>
  bubbles.join(CHAT_SPLIT);

/**
 * 채팅 입력 대화에서 이번 턴 내담자 메시지 수 — 대화 끝에 이어진 "내담자:" 줄들 (서버 창과 같다).
 */
export const chatWindowSize = (lines: string[]): number => {
  let n = 0;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (speakerOf(lines[i]).who !== "patient") break;
    n++;
  }
  return n;
};

export type DiffToken = { text: string; kind: "same" | "del" | "ins" };

// 단어(공백 포함 토큰) 단위 LCS 차이. 채팅 말풍선 구분자는 토큰 하나로 남긴다
const tokenize = (text: string): string[] =>
  text.split(/(<split>|\s+)/).filter((t) => t.length > 0);

/**
 * AI 추천 → 코치 값 차이. ai 쪽은 same/del, coach 쪽은 same/ins 토큰 목록.
 * 긴 글(토큰 1,500개 넘음)은 계산하지 않고 null — 화면이 멈추지 않게.
 */
export const diffTokens = (
  ai: string,
  coach: string
): { ai: DiffToken[]; coach: DiffToken[] } | null => {
  const a = tokenize(ai);
  const b = tokenize(coach);
  if (a.length > 1500 || b.length > 1500) return null;
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const aiOut: DiffToken[] = [];
  const coachOut: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      aiOut.push({ text: a[i], kind: "same" });
      coachOut.push({ text: b[j], kind: "same" });
      i++;
      j++;
    } else if (
      j < b.length &&
      (i >= a.length || lcs[i][j + 1] >= lcs[i + 1][j])
    ) {
      coachOut.push({ text: b[j], kind: "ins" });
      j++;
    } else {
      aiOut.push({ text: a[i], kind: "del" });
      i++;
    }
  }
  return { ai: aiOut, coach: coachOut };
};

/**
 * 빠른 판정 "정답 OK" 가 켜는 학습 — SFT 는 늘, DPO 는 AI 추천과 대비할 가치가 있는 채팅 행(수정·안 씀)만.
 */
export const quickApproveTraining = (detail: {
  feature: AdminAiFeature;
  label: AdminAiLabel;
  aiOutput: AdminAiOutput | null;
}) => ({
  sftEnabled: true,
  dpoEnabled:
    detail.feature === "chat" &&
    !!detail.aiOutput?.text &&
    (detail.label === "edited" || detail.label === "unused"),
  rlEnabled: false,
});

/** 채팅 SFT user 구성 크기 — 에이전트 컨텍스트·대화 글자 수 */
export const chatInputSize = (input: Record<string, unknown> | null) => {
  const context = agentContextOf(input);
  const dialogue = Array.isArray(input?.dialogue)
    ? (input!.dialogue as unknown[]).map(String)
    : [];
  return {
    contextCount: context.length,
    contextChars: context.reduce((sum, c) => sum + c.excerpt.length, 0),
    dialogueCount: dialogue.length,
    dialogueChars: dialogue.reduce((sum, line) => sum + line.length, 0),
  };
};

// 이보다 길면 입력이 길다고 알린다 (글자 수)
export const CHAT_INPUT_WARN_CHARS = 6000;

export type InputLine = { text: string; isTarget: boolean };

// 코멘트 AI 입력 대화에서 코멘트를 달 수 있는 줄 끝에 붙는 [editorKey] 표시
const EDITOR_KEY_MARK = /\[[0-9a-fA-F-]{36}\]/g;

// 입력 스냅샷을 사람이 읽을 줄 목록으로.
// - 자동적 사고: convertDialogue 결과(줄 배열) 그대로
// - 채팅: 직전 대화(서버 dialogue — "내담자: …"/"코치: …"). 없으면(예전 행) 내담자 메시지
// - 코멘트: 서버가 덧붙인 dialogue(코멘트 AI 입력과 같은 대화). [editorKey] 표시는 떼고, 코멘트 위치 줄만 isTarget
export const inputLineItems = (
  feature: AdminAiFeature,
  input: Record<string, unknown> | null
): InputLine[] => {
  if (!input) return [];
  const plain = (lines: string[]) =>
    lines.map((text) => ({ text, isTarget: false }));
  if (feature === "automatic_thought") {
    const dialogue = input.activity_content;
    return Array.isArray(dialogue) ? plain(dialogue.map(String)) : [];
  }
  if (feature === "chat") {
    if (Array.isArray(input.dialogue)) return plain(input.dialogue.map(String));
    const message = input.patient_message;
    return typeof message === "string" ? plain([`내담자: ${message}`]) : [];
  }
  const dialogue = input.dialogue;
  if (!Array.isArray(dialogue)) return [];
  const location = typeof input.location === "string" ? input.location : null;
  const items = dialogue.map(String).map((line) => ({
    text: line.replace(EDITOR_KEY_MARK, ""),
    isTarget: location !== null && line.includes(`[${location}]`),
  }));
  // 위치 줄 뒤에 코멘트가 이미 있으면(다시 주기 시점 본문) [editorKey] 표시가 안 붙는다 — 위치 줄 문장으로 찾는다
  const locationText =
    typeof input.location_text === "string"
      ? normalizeText(input.location_text)
      : "";
  if (locationText && !items.some((item) => item.isTarget)) {
    const i = items
      .map((item) => normalizeText(lineValueOf(item.text)))
      .lastIndexOf(locationText);
    if (i >= 0) items[i] = { ...items[i], isTarget: true };
  }
  return items;
};

const normalizeText = (text: string) => text.replace(/\s+/g, " ").trim();
// "    - 내담자 답변: 값" → "값"
const lineValueOf = (line: string) => line.slice(line.indexOf(":") + 1);

// 코멘트 입력 대화에서 이 행의 코멘트(코치 최종) 줄인지 — 다시 주기 시점 본문에는 이번 회차 코멘트가 들어 있다
export const isRowCommentLine = (line: string, comment: unknown) =>
  typeof comment === "string" &&
  normalizeText(comment) !== "" &&
  line.includes("상담사 코멘트:") &&
  normalizeText(lineValueOf(line)) === normalizeText(comment);

// 대화 줄의 말한 사람 — 자동적 사고·채팅 입력은 "상담사: …" / "내담자: …" (코멘트 위치 표시 [editorKey] 가 붙기도 한다).
// 형식이 다르면(코멘트 기록지 목록 등) who 는 null 이고 줄 그대로 둔다.
const SPEAKER_MARK = /^\s*(상담사|코치|내담자)(?:\[[^\]]*\])?\s*:\s*/;

export const speakerOf = (
  line: string
): { who: "coach" | "patient" | null; text: string } => {
  const match = line.match(SPEAKER_MARK);
  if (!match) return { who: null, text: line };
  return {
    who: match[1] === "내담자" ? "patient" : "coach",
    text: line.slice(match[0].length),
  };
};

// 자동적 사고 입력(생각기록지 → 대화 줄, 서버 ThoughtRecordUtils.convertDialogue)을 항목별로 되돌린다.
// 항목마다 고정 질문 한 줄 + 내담자 기록 한 줄이 오고, 그 뒤에 코치 코멘트(상담사)와 내담자 답이 이어진다.
const THOUGHT_RECORD_PROMPTS: { prefix: string; label: string }[] = [
  { prefix: "감정과 판단을 배제한 객관적인 상황", label: "상황" },
  { prefix: "그 순간에 들었던 날것의 생각", label: "생각" },
  { prefix: "당시 느꼈던 감정", label: "감정" },
  { prefix: "당시 느꼈던 신체 반응", label: "신체 반응" },
  { prefix: "그 사건 이후에 이어진 행동", label: "행동" },
];

export type ThoughtRecordSection = {
  // 형식을 알 수 없는 줄이면 null
  label: string | null;
  entry: string;
  comments: { question: string; answer: string }[];
};

export const thoughtRecordSections = (
  lines: string[]
): ThoughtRecordSection[] => {
  const sections: ThoughtRecordSection[] = [];
  let expectEntry = false;
  lines.forEach((line) => {
    const { who, text } = speakerOf(line);
    const current = sections[sections.length - 1];
    const prompt =
      who === "coach"
        ? THOUGHT_RECORD_PROMPTS.find((p) => text.startsWith(p.prefix))
        : undefined;
    if (prompt) {
      sections.push({ label: prompt.label, entry: "", comments: [] });
      expectEntry = true;
    } else if (who === "patient" && current && expectEntry) {
      // 생각함정별 생각은 " / " 로 이어져 온다 — 한 줄씩 보이게
      current.entry =
        current.label === "생각" ? text.split(" / ").join("\n") : text;
      expectEntry = false;
    } else if (who === "coach" && current) {
      current.comments.push({ question: text, answer: "" });
    } else if (who === "patient" && current?.comments.length) {
      current.comments[current.comments.length - 1].answer = text;
    } else {
      sections.push({ label: null, entry: text.trim(), comments: [] });
    }
  });
  return sections;
};

// 학습 정답이 어디서 오는지 — 보완 칸에 하나라도 있으면 관리자 보완, 아니면 코치 값, 둘 다 없으면 샘플이 안 만들어진다
export const answerSourceOf = (
  coachOutput: AdminAiOutput | null,
  adminOutput: AdminAiOutput | null
): "admin" | "coach" | "none" => {
  if (adminOutput && Object.values(adminOutput).some((v) => v?.trim()))
    return "admin";
  if (coachOutput && Object.values(coachOutput).some((v) => v?.trim()))
    return "coach";
  return "none";
};

export const inputLines = (
  feature: AdminAiFeature,
  input: Record<string, unknown> | null
): string[] => inputLineItems(feature, input).map((line) => line.text);

// 코멘트 행의 위치·유형 — 위치는 editorKey 대신 그 줄의 내담자 문장(서버가 덧붙인 location_text)
export const commentMeta = (input: Record<string, unknown> | null) => ({
  locationText:
    typeof input?.location_text === "string"
      ? input.location_text
      : typeof input?.location === "string"
        ? input.location
        : null,
  commentType:
    typeof input?.comment_type === "string" ? input.comment_type : null,
});

// ---------- 학습 설정 (training_config_json) ----------
// 구조는 프론트가 정한다. 서버는 JSON 객체면 그대로 저장한다.

export type RewardCriterion = {
  key: string;
  label: string;
  how: string;
  on: boolean;
  weight: number;
};

export type TrainingConfig = {
  sft: { systemPromptVersion: string };
  dpo: {
    // chosen 은 항상 정답(코치 + 관리자 보완)
    rejected: "ai" | "custom";
    rejectedCustom: string;
    // 자동적 사고만: 한 쌍(전체) / 바뀐 항목마다 한 쌍
    pairUnit: "whole" | "field";
  };
  rl: {
    usage: "train" | "eval";
    grading: "rule" | "llm" | "mixed";
    rewards: RewardCriterion[];
    rubric: string;
  };
};

const DEFAULT_REWARDS: Record<AdminAiFeature, Omit<RewardCriterion, "on">[]> = {
  automatic_thought: [
    {
      key: "format",
      label: "형식 준수",
      how: "항목 키가 모두 있고 값이 비어 있지 않은지 (규칙)",
      weight: 1,
    },
    {
      key: "thinking_trap",
      label: "생각함정 일치",
      how: "정답의 생각함정과 같은지 (규칙)",
      weight: 2,
    },
    {
      key: "similarity",
      label: "항목 유사도",
      how: "항목별로 정답과 얼마나 가까운지 (편집 거리·임베딩)",
      weight: 1,
    },
    {
      key: "voice",
      label: "내담자 어투",
      how: "생각을 내담자의 말투·개념화 문장으로 썼는지 (LLM 심사)",
      weight: 1,
    },
  ],
  chat: [
    {
      key: "similarity",
      label: "정답 유사도",
      how: "보낸 메시지와 얼마나 가까운지 (편집 거리·임베딩)",
      weight: 1,
    },
    {
      key: "tone",
      label: "코치 어투",
      how: "코치의 말투·호칭 규칙을 지켰는지 (LLM 심사)",
      weight: 1,
    },
  ],
  thought_record_comment: [
    {
      key: "similarity",
      label: "정답 유사도",
      how: "전달된 코멘트와 얼마나 가까운지 (편집 거리·임베딩)",
      weight: 1,
    },
    {
      key: "purpose",
      label: "코멘트 목적",
      how: "위치에 맞는 질문·피드백인지 (LLM 심사)",
      weight: 1,
    },
  ],
};

export const defaultTrainingConfig = (
  feature: AdminAiFeature
): TrainingConfig => ({
  sft: { systemPromptVersion: "v0" },
  dpo: { rejected: "ai", rejectedCustom: "", pairUnit: "whole" },
  rl: {
    usage: "train",
    grading: "mixed",
    rewards: DEFAULT_REWARDS[feature].map((r) => ({ ...r, on: true })),
    rubric: "",
  },
});

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

// 저장된 설정을 기본값 위에 얹는다 — 비어 있거나 일부만 있어도 폼이 깨지지 않게.
// 보상 기준은 기본 목록을 기준으로 저장된 on/weight 만 덮어쓴다 (기준 목록이 바뀌어도 화면이 유지된다).
export const parseTrainingConfig = (
  feature: AdminAiFeature,
  saved: Record<string, unknown> | null
): TrainingConfig => {
  const base = defaultTrainingConfig(feature);
  if (!saved) return base;
  const sft = isObject(saved.sft) ? saved.sft : {};
  const dpo = isObject(saved.dpo) ? saved.dpo : {};
  const rl = isObject(saved.rl) ? saved.rl : {};
  const savedRewards = Array.isArray(rl.rewards)
    ? rl.rewards.filter(isObject)
    : [];
  return {
    sft: {
      systemPromptVersion:
        typeof sft.systemPromptVersion === "string"
          ? sft.systemPromptVersion
          : base.sft.systemPromptVersion,
    },
    dpo: {
      rejected: dpo.rejected === "custom" ? "custom" : "ai",
      rejectedCustom:
        typeof dpo.rejectedCustom === "string" ? dpo.rejectedCustom : "",
      pairUnit: dpo.pairUnit === "field" ? "field" : "whole",
    },
    rl: {
      usage: rl.usage === "eval" ? "eval" : "train",
      grading:
        rl.grading === "rule" || rl.grading === "llm" ? rl.grading : "mixed",
      rewards: base.rl.rewards.map((r) => {
        const s = savedRewards.find((x) => x.key === r.key);
        return {
          ...r,
          on: typeof s?.on === "boolean" ? s.on : r.on,
          weight: typeof s?.weight === "number" ? s.weight : r.weight,
        };
      }),
      rubric: typeof rl.rubric === "string" ? rl.rubric : "",
    },
  };
};

// 저장할 때는 화면용 설명(label·how)을 빼고 값만 남긴다
export const serializeTrainingConfig = (config: TrainingConfig) => ({
  sft: config.sft,
  dpo: config.dpo,
  rl: {
    ...config.rl,
    rewards: config.rl.rewards.map(({ key, on, weight }) => ({
      key,
      on,
      weight,
    })),
  },
});

// ---------- 미리보기 ----------
// 실제 내보내기(JSONL)는 서버 몫이다. 여기서는 이 행이 어떤 모양의 샘플이 되는지만 보여준다.
// system 프롬프트 본문은 외부 AI 서비스에 있어 버전 이름만 표시한다.

const nonEmpty = (output: AdminAiOutput) =>
  Object.fromEntries(
    Object.entries(output).filter(([, v]) => v != null && v !== "")
  );

// 코멘트는 서버가 만든 학습 입력(sample_dialogue) — 코멘트 가능한 줄에 [editorKey], 이번 회차 코멘트(정답)는 뺀 본문
// 채팅은 에이전트 컨텍스트(있으면) + 직전 대화
export const userContent = (detail: AdminAiDatasetDetail) => {
  const sample = detail.input?.sample_dialogue;
  if (detail.feature === "thought_record_comment" && Array.isArray(sample)) {
    return sample.map(String).join("\n");
  }
  const dialogue = inputLines(detail.feature, detail.input).join("\n");
  if (detail.feature !== "chat") return dialogue;
  const context = agentContextOf(detail.input);
  if (context.length === 0) return dialogue;
  const contextText = context
    .map((c) => `(${c.kind} ${c.target})\n${c.excerpt}`)
    .join("\n\n");
  return `[에이전트 컨텍스트]\n${contextText}\n\n[대화]\n${dialogue}`;
};

// 코멘트는 모델이 위치도 고르도록 코멘트 LLM 잡 출력과 같은 {location, comment}
const assistantContent = (
  detail: AdminAiDatasetDetail,
  output: AdminAiOutput
): string => {
  if (detail.feature === "automatic_thought") {
    return JSON.stringify(nonEmpty(output));
  }
  if (detail.feature === "thought_record_comment") {
    const location = detail.input?.location;
    return JSON.stringify({
      location: typeof location === "string" ? location : null,
      comment: output.text ?? "",
    });
  }
  return output.text ?? "";
};

// SFT 샘플 — 안 씀 행은 정답이 없어 샘플이 되지 않는다
export const sftSample = (
  detail: AdminAiDatasetDetail,
  adminOutput: AdminAiOutput | null,
  systemPromptVersion: string
) => {
  const answer = answerOf(detail.coachOutput, adminOutput);
  if (Object.keys(nonEmpty(answer)).length === 0) return null;
  return {
    messages: [
      { role: "system", content: `<system prompt ${systemPromptVersion}>` },
      { role: "user", content: userContent(detail) },
      {
        role: "assistant",
        content: assistantContent(detail, answer),
      },
    ],
  };
};

// DPO 쌍 — chosen 은 정답, rejected 는 AI 초안 또는 직접 입력.
// 항목별 쌍은 자동적 사고에서 정답과 AI 가 다른 항목마다 하나씩 만든다.
export const dpoPairs = (
  detail: AdminAiDatasetDetail,
  adminOutput: AdminAiOutput | null,
  dpo: TrainingConfig["dpo"]
): { title: string; chosen: string; rejected: string }[] => {
  const answer = answerOf(detail.coachOutput, adminOutput);
  const ai = detail.aiOutput ?? {};
  if (Object.keys(nonEmpty(answer)).length === 0) return [];

  if (dpo.rejected === "custom") {
    if (!dpo.rejectedCustom.trim()) return [];
    return [
      {
        title: "전체",
        chosen: assistantContent(detail, answer),
        rejected:
          detail.feature === "thought_record_comment"
            ? assistantContent(detail, { text: dpo.rejectedCustom })
            : dpo.rejectedCustom,
      },
    ];
  }

  if (detail.feature === "automatic_thought" && dpo.pairUnit === "field") {
    return AUTOMATIC_THOUGHT_FIELDS.filter(
      ({ key }) => (answer[key] ?? "") !== (ai[key] ?? "") && !!answer[key]
    ).map(({ key, label }) => ({
      title: label,
      chosen: answer[key] ?? "",
      rejected: ai[key] ?? "",
    }));
  }

  const chosen = assistantContent(detail, answer);
  const rejected = assistantContent(detail, ai);
  // 같으면 대비할 게 없다
  return chosen === rejected ? [] : [{ title: "전체", chosen, rejected }];
};
